import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { LanguageContext } from '../App';
import { translations } from '../i18n/translations';
import { apiConfig } from '../config/authConfig';
import { theme } from '../config/theme';
import Breadcrumb from './Breadcrumb';
import FileModal from './FileModal';
import SearchBar from './SearchBar';
import OfficeViewer from './OfficeViewer';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

function Explorer() {
  const { containerName } = useParams();
  const { instance, accounts } = useMsal();
  const { language, setLanguage } = useContext(LanguageContext);
  const t = translations[language];
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [prefix, setPrefix] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isOfficeFile, setIsOfficeFile] = useState(false);
  const [officeToken, setOfficeToken] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  // Refetch quand on change de dossier OU de vue (la mosaique demande les apercus)
  useEffect(() => { fetchFiles(prefix, viewMode === 'mosaic'); }, [prefix, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const getToken = async () => {
    const tokenResponse = await instance.acquireTokenSilent({
      scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/access_as_user`],
      account: accounts[0],
    });
    return tokenResponse.accessToken;
  };

  const triggerDownload = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isImageFile = (item) =>
    item.type === 'file' && IMAGE_EXTS.includes(item.name.split('.').pop().toLowerCase());

  const fetchFiles = async (currentPrefix, withThumbs) => {
    setLoading(true); setError(null); setSelectedItems([]);
    try {
      const token = await getToken();
      const url = `${apiConfig.baseUrl}/list-files?container=${encodeURIComponent(containerName)}&prefix=${encodeURIComponent(currentPrefix)}${withThumbs ? '&thumbnails=true' : ''}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json();
      setItems(data.items || []);
    } catch (err) { setError(t.error); }
    finally { setLoading(false); }
  };

  const handleItemClick = async (item) => {
    if (item.type === 'folder') { setPrefix(item.path); setSearchResults(null); }
    else { await openFile(item); }
  };

  const openFile = async (item) => {
    const ext = item.name.split('.').pop().toLowerCase();
    const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    const previewExts = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const isDownload = !officeExts.includes(ext) && !previewExts.includes(ext);
    try {
      const token = await getToken();
      const response = await fetch(
        `${apiConfig.baseUrl}/get-file?container=${encodeURIComponent(containerName)}&path=${encodeURIComponent(item.path)}${isDownload ? '&download=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      const sasUrl = data.url;
      if (officeExts.includes(ext)) {
        setSelectedFile(item); setFileUrl(sasUrl); setOfficeToken(token); setIsOfficeFile(true);
      } else if (previewExts.includes(ext)) {
        setSelectedFile(item); setFileUrl(sasUrl); setIsOfficeFile(false);
      } else {
        triggerDownload(sasUrl, item.name);
      }
    } catch (err) { setError(t.error); }
  };

  const handleSearch = async (query) => {
    setIsSearching(true); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(
        `${apiConfig.baseUrl}/search?container=${encodeURIComponent(containerName)}&q=${encodeURIComponent(query)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) { setError(t.error); }
    finally { setIsSearching(false); }
  };

  const handleClearSearch = () => setSearchResults(null);

  const toggleSelect = (item) => {
    setSelectedItems((prev) =>
      prev.find((i) => i.path === item.path)
        ? prev.filter((i) => i.path !== item.path)
        : [...prev, item]
    );
  };

  const handleDownloadSelected = async () => {
    // Un seul fichier : telechargement direct
    if (selectedItems.length === 1) {
      const item = selectedItems[0];
      try {
        const token = await getToken();
        const response = await fetch(
          `${apiConfig.baseUrl}/get-file?container=${encodeURIComponent(containerName)}&path=${encodeURIComponent(item.path)}&download=true`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        triggerDownload(data.url, item.name);
      } catch (err) { setError(t.error); }
      return;
    }
    // Plusieurs fichiers : zip global, telechargement natif par le navigateur
    try {
      const token = await getToken();
      const response = await fetch(`${apiConfig.baseUrl}/prepare-zip`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          container: containerName,
          paths: selectedItems.map((i) => i.path),
        }),
      });
      if (!response.ok) throw new Error('prepare failed');
      const data = await response.json();
      // iframe cachee : le navigateur telecharge nativement (progression + ecriture
      // directe sur le disque) sans bufferiser le zip en memoire JS.
      const downloadUrl = `${apiConfig.baseUrl}/download-zip?token=${encodeURIComponent(data.token)}`;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = downloadUrl;
      document.body.appendChild(iframe);
      setTimeout(() => document.body.removeChild(iframe), 120000);
    } catch (err) { setError(t.error); }
  };

  const handleLogout = () => instance.logoutRedirect().catch(console.error);

  const folderIcon = (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" fill={theme.colors.accent}/>
    </svg>
  );

  const fileIcon = (size) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill={theme.colors.textMuted}/>
      <path d="M14 2v6h6" stroke="#444" strokeWidth="1"/>
    </svg>
  );

  const getFileIcon = (item) => (item.type === 'folder' ? folderIcon(16) : fileIcon(16));

  const displayItems = searchResults !== null ? searchResults : items;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logoGrid}>
            {[...Array(9)].map((_, i) => <span key={i} style={styles.logoCell} />)}
          </div>
          <span style={styles.headerTitle}>Archives</span>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.langSelector}>
            <button onClick={() => setLanguage('fr')}
              style={language === 'fr' ? styles.langActive : styles.langBtn}>FR</button>
            <button onClick={() => setLanguage('en')}
              style={language === 'en' ? styles.langActive : styles.langBtn}>EN</button>
          </div>
          <span style={styles.userName}>{accounts[0]?.name || accounts[0]?.username}</span>
          <button onClick={handleLogout} style={styles.logoutBtn}>{t.logout}</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.topBar}>
          <button onClick={() => navigate('/home')} style={styles.backBtn}>← {t.back}</button>
          <div style={styles.topBarRight}>
            <div style={styles.viewToggle}>
              <button onClick={() => setViewMode('list')}
                style={viewMode === 'list' ? styles.viewBtnActive : styles.viewBtn}>{t.viewList}</button>
              <button onClick={() => setViewMode('mosaic')}
                style={viewMode === 'mosaic' ? styles.viewBtnActive : styles.viewBtn}>{t.viewMosaic}</button>
            </div>
            {selectedItems.length > 0 && (
              <button onClick={handleDownloadSelected} style={styles.downloadBtn}>
                ⬇ {t.downloadSelected} ({selectedItems.length})
              </button>
            )}
          </div>
        </div>

        <Breadcrumb container={containerName} prefix={prefix} onNavigate={(path) => setPrefix(path)} />
        <SearchBar onSearch={handleSearch} onClear={handleClearSearch} />

        {searchResults !== null && (
          <p style={styles.searchTitle}>
            {t.searchResults} — {searchResults.length} {searchResults.length > 1 ? 'fichiers' : 'fichier'}
          </p>
        )}

        {loading && <p style={styles.info}>{t.loading}</p>}
        {error && <p style={styles.errorMsg}>{error}</p>}
        {isSearching && <p style={styles.info}>{t.loading}</p>}
        {!loading && !error && displayItems.length === 0 && (
          <p style={styles.info}>{searchResults !== null ? t.noResults : t.noContainers}</p>
        )}

        {!loading && viewMode === 'list' && (
          <div style={styles.fileList}>
            {displayItems.map((item) => (
              <div key={item.path} style={styles.fileRow}>
                {item.type === 'file' && (
                  <input type="checkbox"
                    checked={!!selectedItems.find((i) => i.path === item.path)}
                    onChange={() => toggleSelect(item)}
                    style={styles.checkbox}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div style={styles.fileItem}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={e => { e.currentTarget.style.background = theme.colors.bgCardHover; e.currentTarget.style.borderColor = theme.colors.borderHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.colors.bgCard; e.currentTarget.style.borderColor = theme.colors.border; }}
                >
                  <span style={styles.fileIconWrap}>{getFileIcon(item)}</span>
                  <span style={styles.fileName}>{item.name}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && viewMode === 'mosaic' && (
          <div style={styles.mosaicGrid}>
            {displayItems.map((item) => (
              <div key={item.path} style={styles.mosaicCard}
                onClick={() => handleItemClick(item)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.colors.borderHover; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.colors.border; }}
              >
                {item.type === 'file' && (
                  <input type="checkbox"
                    checked={!!selectedItems.find((i) => i.path === item.path)}
                    onChange={() => toggleSelect(item)}
                    style={styles.mosaicCheckbox}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <div style={styles.mosaicThumbWrap}>
                  {isImageFile(item) && item.url
                    ? <img src={item.url} alt={item.name} loading="lazy" style={styles.mosaicImg} />
                    : (item.type === 'folder' ? folderIcon(44) : fileIcon(44))}
                </div>
                <div style={styles.mosaicLabel}>{item.name}</div>
              </div>
            ))}
          </div>
        )}
      </main>

      {selectedFile && fileUrl && !isOfficeFile && (
        <FileModal file={selectedFile} url={fileUrl}
          onClose={() => { setSelectedFile(null); setFileUrl(null); setIsOfficeFile(false); }} />
      )}
      {selectedFile && fileUrl && isOfficeFile && (
        <OfficeViewer file={selectedFile} url={fileUrl} token={officeToken}
          onClose={() => { setSelectedFile(null); setFileUrl(null); setIsOfficeFile(false); setOfficeToken(null); }} />
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: theme.colors.bg },
  header: {
    backgroundColor: theme.colors.bgHeader,
    padding: '0 24px', height: '56px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  logoGrid: {
    width: '28px', height: '28px', backgroundColor: theme.colors.white,
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr 1fr',
    gap: '2px', padding: '4px', borderRadius: '2px',
  },
  logoCell: { backgroundColor: theme.colors.textDark, display: 'block', borderRadius: '1px' },
  headerTitle: {
    color: theme.colors.white, fontSize: theme.fonts.size.base,
    fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  langSelector: { display: 'flex', gap: '4px' },
  langBtn: {
    border: `1px solid ${theme.colors.border}`, borderRadius: '3px',
    padding: '3px 8px', cursor: 'pointer', backgroundColor: 'transparent',
    color: theme.colors.textSecondary, fontSize: theme.fonts.size.xs,
  },
  langActive: {
    border: `1px solid ${theme.colors.white}`, borderRadius: '3px',
    padding: '3px 8px', cursor: 'pointer', backgroundColor: theme.colors.white,
    color: theme.colors.textDark, fontSize: theme.fonts.size.xs, fontWeight: '600',
  },
  userName: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  logoutBtn: {
    backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '5px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm,
  },
  main: { padding: '32px' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  topBarRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  backBtn: {
    backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '6px 14px', cursor: 'pointer', fontSize: theme.fonts.size.base,
  },
  viewToggle: { display: 'flex', gap: '4px' },
  viewBtn: {
    backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px', color: theme.colors.textSecondary,
    padding: '6px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm,
  },
  viewBtnActive: {
    backgroundColor: theme.colors.white, border: `1px solid ${theme.colors.white}`,
    borderRadius: '4px', color: theme.colors.textDark,
    padding: '6px 12px', cursor: 'pointer', fontSize: theme.fonts.size.sm, fontWeight: '600',
  },
  downloadBtn: {
    backgroundColor: theme.colors.white, color: theme.colors.textDark,
    border: 'none', borderRadius: '4px', padding: '6px 14px',
    cursor: 'pointer', fontSize: theme.fonts.size.sm, fontWeight: '600',
  },
  searchTitle: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base, marginBottom: '16px' },
  info: { color: theme.colors.textSecondary, fontSize: theme.fonts.size.base },
  errorMsg: { color: '#e74c3c', fontSize: theme.fonts.size.base },

  fileList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  fileRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  checkbox: { cursor: 'pointer', width: '14px', height: '14px', accentColor: theme.colors.accent },
  fileItem: {
    flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 14px', backgroundColor: theme.colors.bgCard,
    border: `1px solid ${theme.colors.border}`, borderRadius: '6px',
    cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
  },
  fileIconWrap: { width: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  fileName: { fontSize: theme.fonts.size.base, color: '#d0d0d0' },

  mosaicGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '12px',
  },
  mosaicCard: {
    position: 'relative',
    backgroundColor: theme.colors.bgCard,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 0.15s',
  },
  mosaicCheckbox: {
    position: 'absolute', top: '8px', left: '8px', zIndex: 2,
    width: '16px', height: '16px', accentColor: theme.colors.accent, cursor: 'pointer',
  },
  mosaicThumbWrap: {
    width: '100%', height: '130px',
    backgroundColor: '#0d0d0d',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  mosaicImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  mosaicLabel: {
    padding: '8px 10px', fontSize: theme.fonts.size.sm, color: theme.colors.textPrimary,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
};

export default Explorer;
